import React from 'react';
import { getYear } from 'date-fns';
import useModal from '@/hooks/useModal';
import { Typography, RadioButton, Button } from '@/components/common';
import { InputCheck, InputDate, Textarea } from '@/components/form';
import { useForm } from 'react-hook-form';
import { CaseQuestionI, SendFollowUpPayload } from '@/interfaces';
import { ModalHeader } from '../ModalHeader';
import Icons from '@/const/icons';

interface Props {
	title: string;
	questions: CaseQuestionI[];
	isOpen: boolean;
	heading?: string;
	description?: string;
	isPreview?: boolean;
	isViewOnly?: boolean;
	onClose?: () => void;
	onSubmit?: (data: SendFollowUpPayload) => void | Promise<void>;
	customFooter?: React.ReactNode;
	renderFooter?: (
		handleSubmit: () => void,
		isSubmitting: boolean
	) => React.ReactNode;
	withoutModal?: boolean;
	classNameContainer?: string;
}

export const SurveyQuestionsModal: React.FC<Props> = ({
	title,
	questions,
	isOpen,
	heading,
	description,
	onClose,
	isPreview = false,
	onSubmit,
	isViewOnly = false,
	customFooter,
	renderFooter,
	withoutModal = false,
	classNameContainer,
}) => {
	const { Modal, show, hide } = useModal();
	const {
		register,
		setValue,
		watch,
		reset,
		formState: { isSubmitting },
	} = useForm();
	const formValues = watch();

	const [isSubmittingForm, setIsSubmittingForm] = React.useState(false);

	const allFilled = questions.every(
		({
			id,
			question_type,
			multiple_choise_questions,
			ask_explanation,
			incorrect_answer,
		}) => {
			let answered = false;

			if (question_type === 'YES_NO') {
				answered = !!formValues[`answers_${id}`];
			} else if (question_type === 'TEXT') {
				answered = !!formValues[`answers_${id}`]?.trim();
			} else if (question_type === 'DATE') {
				answered = !!formValues[`answers_${id}`];
			} else if (question_type === 'MULTIPLE') {
				answered =
					multiple_choise_questions?.some(
						(option) => !!formValues[`answer_${option.id}`]
					) ?? false;
			}

			const incorrectValue = incorrect_answer ?? 'NO';
			if (ask_explanation && formValues[`answers_${id}`] === incorrectValue) {
				answered = answered && !!formValues[`explanation_${id}`]?.trim();
			}

			return answered;
		}
	);

	React.useEffect(() => {
		if (isOpen) show();
	}, [isOpen]);

	React.useEffect(() => {
		reset();
		questions.forEach(
			({
				id,
				question_type,
				multiple_choise_questions,
				answer,
				explanation_answer,
			}) => {
				if (question_type === 'MULTIPLE') {
					multiple_choise_questions?.forEach((option) => {
						if (option.answer) {
							setValue(`answer_${option.id}`, true);
						}
					});
				} else if (question_type === 'YES_NO' && answer) {
					setValue(`answers_${id}`, answer.toUpperCase());
				}

				if (explanation_answer) {
					setValue(`explanation_${id}`, explanation_answer);
				}
			}
		);
	}, [questions, reset, setValue]);

	const handleClose = () => {
		hide();
		onClose?.();
	};

	const handleSubmit = async () => {
		if (!onSubmit) return;

		setIsSubmittingForm(true);

		const answers = questions.map(
			({ id, question_type, multiple_choise_questions }) => {
				let answer = '';
				let answers: string[] = [];

				if (question_type === 'MULTIPLE') {
					answers = (multiple_choise_questions ?? [])
						.filter((opt) => !!formValues[`answer_${opt.id}`])
						.map((opt) => opt.brand || '');
				} else {
					answer = formValues[`answers_${id}`] ?? '';
					if (question_type === 'YES_NO' && answer) {
						const selected = answer;
						const matchedOption = multiple_choise_questions?.find(
							(opt) => opt.brand?.toUpperCase() === selected.toUpperCase()
						);
						answer =
							matchedOption?.brand ??
							answer.charAt(0).toUpperCase() + answer.slice(1).toLowerCase();
					}
				}

				return {
					question_id: id,
					answer,
					detail: formValues[`explanation_${id}`] ?? '',
					answers,
				};
			}
		);

		try {
			await onSubmit({ answers });
		} finally {
			setIsSubmittingForm(false);
		}
	};

	const content = (
		<>
			{withoutModal && title && (
				<ModalHeader
					title={title}
					icon={Icons.documentsEmptyState}
					classNameContent="!justify-start"
					classNameImage="!w-14 !h-14"
				/>
			)}
			<div
				className={`pr-2 ${
					withoutModal
						? 'flex-1 min-h-0 overflow-y-auto scroll-custom'
						: 'mt-6 max-h-[595px] overflow-y-auto scroll-custom'
				}`}
			>
				<div
					className="rounded-[32px] p-8"
					style={{ backgroundColor: '#F5EEFF' }}
				>
					{heading && (
						<Typography type="semibold-20" className="text-left">
							{heading}
						</Typography>
					)}
					{description && (
						<Typography
							type="regular-16"
							className="text-left mt-3 max-w-[551px]"
						>
							{description}
						</Typography>
					)}

					<div className="mt-6 space-y-6">
						{questions.map(
							(
								{
									id,
									question,
									question_type,
									multiple_choise_questions,
									ask_explanation,
									incorrect_answer,
									answer,
									explanation_answer,
								},
								index
							) => (
								<div key={id} className="space-y-2">
									<Typography
										type="semibold-16"
										className="text-left flex gap-2"
									>
										<span>{index + 1}.</span>
										<span>{question}</span>
									</Typography>

									{question_type === 'YES_NO' && (
										<>
											<input type="hidden" {...register(`answers_${id}`)} />
											<RadioButton
												disabled={isViewOnly}
												defaultValue={answer?.toUpperCase()}
												options={[
													{ id: `q_${id}_yes`, value: 'YES', label: 'Yes' },
													{ id: `q_${id}_no`, value: 'NO', label: 'No' },
												]}
												orientation="vertical"
												value={watch(`answers_${id}`)}
												onChange={(value) =>
													setValue(`answers_${id}`, value, {
														shouldDirty: true,
													})
												}
												className="min-h-16"
												classNameLabel="!text-gray-35"
											/>
										</>
									)}

									{question_type === 'TEXT' && (
										<Textarea
											name={`answers_${id}`}
											register={register}
											defaultValue={answer}
											customPlaceholder="Write your answer here"
											classNameInput="!mt-0 disabled:!text-black"
											disabled={isViewOnly}
										/>
									)}

									{question_type === 'MULTIPLE' &&
										multiple_choise_questions?.map((option) => (
											<div key={option.id} className="flex items-center gap-4">
												<InputCheck
													name={`answer_${option.id}`}
													register={register}
													className="bg-white w-6"
												/>
												<div className="flex items-center">
													{option.brand && (
														<Typography
															type="regular-16"
															className="!text-gray-35"
														>
															{option.brand}
														</Typography>
													)}
													{option.complement && (
														<Typography
															type="regular-16"
															className="!text-gray-35"
														>
															{`${option.brand ? ':' : ''} ${
																option.complement
															}`}
														</Typography>
													)}
												</div>
											</div>
										))}

									{question_type === 'DATE' && (
										<InputDate
											dateRange="before-current"
											startYear={getYear(new Date()) - 100}
											endYear={getYear(new Date())}
											name={`answers_${id}`}
											register={register}
											setValueInput={setValue}
											className="w-full"
											classNameTrigger="w-full"
											classNameIcon="!top-[10px]"
											classNameInput="!placeholder-[#0D061633] !text-black !mt-0"
											placeholder="MM/DD/YYYY"
											onSelected={() => console.log('')}
											dateDefault={answer ?? watch(`answers_${id}`)}
											disabled={isViewOnly}
										/>
									)}

									{(ask_explanation &&
										(isPreview ||
											watch(`answers_${id}`) === (incorrect_answer ?? 'NO'))) ||
									(isViewOnly && !!explanation_answer) ? (
										<Textarea
											placeholder="Please explain your answer."
											name={`explanation_${id}`}
											register={register}
											className="max-w-[326px]"
											defaultValue={explanation_answer}
											disabled={isViewOnly}
											classNameInput="!mt-0 disabled:!text-black"
											rows={2}
											maxLength={80}
										/>
									) : null}
								</div>
							)
						)}
					</div>
				</div>
			</div>
			{renderFooter
				? renderFooter(handleSubmit, isSubmittingForm)
				: customFooter ??
				  (!isViewOnly && (
						<div className="flex justify-end mt-4">
							<Button
								label="Submit"
								size="medium"
								className="!w-[237px]"
								decoration="primary"
								disabled={
									!allFilled || isPreview || isSubmitting || isSubmittingForm
								}
								loading={isSubmittingForm}
								onClick={handleSubmit}
							/>
						</div>
				  ))}
		</>
	);

	if (withoutModal) {
		return (
			<div
				className={`flex flex-col w-full h-full ${classNameContainer ?? ''}`}
			>
				{content}
			</div>
		);
	}

	return (
		<Modal
			avoidClose
			title={
				<ModalHeader
					title={title}
					icon={Icons.documentsEmptyState}
					classNameContent="!justify-start"
					classNameImage="!w-14 !h-14"
				/>
			}
			onClose={onClose ? handleClose : undefined}
			withXButton={!!onClose}
			noMaxHeight
			classNameTitle="!mb-0 w-full"
			classNameCardContainer="w-full md:!px-8 md:!py-8 md:min-w-[695px]"
			classNameContainerChildren="flex flex-col overflow-visible"
		>
			{content}
		</Modal>
	);
};
